import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { IS_SANDBOX } from "@bridgesplit/utils";

import { LendingVaultMetadata } from "./vault";
import { Mint } from "./alias";
export interface RewardsFilter {
    feVisible: boolean;
    activeOnly: boolean;
}

export interface UserPoints {
    points: number;
    pps: number;
    timestamp: number;
    betaPoints: number;
}
export interface UserPointsCalculationInfo {
    basePoints: UserPoints;
    referralPoints: UserPoints;
}

export interface UserPastPoints {
    basePoints: number;
    referralBonusPoints: number;
}

export interface UserPointsPerSecond {
    basePointsPerSecond: number;
    referralPointsPerSecond: number;
}

export interface PointMultiplier {
    reward: LoopscalePointsAction;
    multiplier: number;
    mint: string;
    name: string;
}

export enum PointsRewardType {
    SignUp = 0,
    BetaSignup = 1,
    FollowTwitter = 2,
    JoinDiscord = 3,
    CommunityEngagement = 4,
    AcceptReferral = 7
}

export interface PointsReward {
    reward: RewardValue;
    rewardName: string;
    pointsPerSecond: number;
    startTime: number;
    endTime: number | null;
    feRewardLabel: string;
    feVisible: boolean;
    feDisabled: boolean;
    feUrl: string;
    feContinuous: boolean;
}

export interface UserPointsReward {
    id: number;
    userIdentifier: string;
    reward: PointsRewardType;
    startTime: number;
    endTime: number | null;
    multiplier: number;
}

export enum CommunityMultiplierType {
    Ovols = 0
}

export interface CommunityMultiplier {
    communityMultiplier: CommunityMultiplierType;
    multiplier: number;
}

export interface CommunityRewardClaim {
    reward: number;
    userIdentifier: string;
    attemptedClaim: boolean;
}

export enum CommunityRewardsType {
    Token = 0,
    Nft = 1,
    OrcaLp = 2
}

export enum ExternalPointSource {
    MarginFi = 0,
    Loopscale = 1,
    Jupiter = 2,
    Meteora = 3,
    HubSol = 4,
    Solayer = 5,
    Adrastea = 6,
    Flash = 7,
    RateX = 8,
    LoopscaleHidden = 9
}

//Cleaner rewards set up
export enum ExternalPointReward {
    MarginFi = 0,
    Jupiter = 2,
    Meteora = 3,
    Solayer = 5,
    Adrastea = 6,
    Flash = 7,
    RateX = 8,
    Fragmetric = 9,
    Stabble = 10
}

export type RewardValue = PointsRewardType | LoopscalePointsAction;

export enum LoopscalePointsAction {
    Borrow = IS_SANDBOX ? 17 : 35,
    Lend = IS_SANDBOX ? 18 : 36,
    IdleCap = IS_SANDBOX ? 19 : 37,
    LiquidStake = IS_SANDBOX ? 45 : 79,
    ThirtyDayVest = IS_SANDBOX ? 46 : 80,
    SixtyDayVest = IS_SANDBOX ? 47 : 81,
    NintyDayVest = IS_SANDBOX ? 48 : 82,
    OneHundredEightyDayVest = IS_SANDBOX ? 49 : 83,
    GenesisLiquidStake = IS_SANDBOX ? 51 : 94,
    LendVaultLoyalty = IS_SANDBOX ? 56 : 100,
    IdleLendLoyalty = IS_SANDBOX ? 57 : 101,
    ActiveLendLoyalty = IS_SANDBOX ? 58 : 102
}

//Response from ROUTE_PORTFOLIO_REWARDS
export interface PortfolioRewards {
    strategies: Record<string, StrategyRewards>;
    loans: Record<string, LedgerReward[]>;
    vaultStakes: Record<string, VaultReward>;
    loops: Record<string, LedgerReward[]>;
}

export interface StrategyRewards {
    ledgerRewards: LedgerReward[];
    externalYieldRewards?: ExternalYieldReward;
}

export interface BasePortfolioReward {
    rewardId: number;
    reward: number;
    pointsPerSecond: number; // points per second = multiplier * usd_value
    accruedTimeStamp: number;
    lastUpdateValue: number;
    usdValue: number;
    isActive: boolean;
    endTime: number | null;
    isLenderReward: boolean;
    multiplier: number;
}

export interface LedgerReward {
    baseReward: BasePortfolioReward;
    loanCollateralAssetIdentifiers: string[];
    principalMint: string;
    loanAddress: string;
    strategyAddress: string;
}

export interface ExternalYieldReward {
    baseReward: BasePortfolioReward;
    strategyAddress: string;
    externalYieldSource: number;
    principalMint: string;
}

export interface VaultReward {
    baseReward: BasePortfolioReward;
    vaultName: string;
    vaultMetadata: LendingVaultMetadata;
    stakeAddress: string;
    vaultPrincipalMint: string;
}

export interface PortfolioRewardsFilter {
    loanAddresses: string[];
    strategyAddresses: string[];
    stakeAddresses: string[];
    isActive?: boolean;
}

export interface CollateralPointsData {
    eligibleMultipliers: LoopscalePointsAction[];
    showRewards: boolean;
    getAdditionalPoints: (pointSources: LoopscalePointsAction[]) => number;
}

export interface MultiplierInfo {
    pointSourceToAdditionalMultipliers: Record<LoopscalePointsAction, number>; // PointSource (Lend : 4x)
    name: string;
}

export interface UseRewardsCalculationProps {
    externalPointSources: ExternalPointSource[];
    loopscalePointsAction?: LoopscalePointsAction;
    metadata?: TokenListMetadata[];
    loopLeverage?: number;
}

export interface TokenSourceData {
    eligibleMultipliers: LoopscalePointsAction[];
    showRewards: boolean;
}

export interface PointsInfo {
    externalPointSource: ExternalPointSource;
    multiplier: number | undefined;
    caption: string;
}
export type PointsInfoDetails = Omit<PointsInfo, "multiplier">;

export enum LabelVisibility {
    Hidden = "hidden", // Hides entire component
    ShowBasic = "basic", // Shows just text
    ShowFull = "full" // Shows text + rewards icon
}

export enum BreakdownActivity {
    AdvancedLend,
    LendVault,
    Borrow,
    IdleLend,
    Loop
}
export interface PortfolioRewardsBreakdown {
    rewardId: number;
    collateralMetadata?: TokenListMetadata[];
    principalMetadata: TokenListMetadata;
    activity: BreakdownActivity;
    pointsPerDay: number;
    boost: number | undefined;
    totalPoints: number;
    usdValue: number;
    isActive: boolean;
    // Additional fields for Vaults, and External Yield
    vaultMetadata?: LendingVaultMetadata;
    externalYieldSource?: number;
}

export interface LoanMultiplier {
    [key: Mint]: Record<LoopscalePointsAction, LoanMultiplierInfo>;
}

export interface LoanMultiplierInfo {
    name: string;
    multiplier: number;
}

export type RewardPointsPerSecond = Record<
    LoopscalePointsAction,
    { pointsPerSecond: number; relativePointsPerSecond: number; rewardLabel: string }
>;
